import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Stack,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { api } from "../api";
import {
  countBy,
  summarize,
  trendByDay,
  withinDays,
  entryPages,
  newVsReturning,
  sessionStats,
  sessionsByDay,
  formatDuration,
} from "./aggregate";
import { StatTile, BarList, DonutChart, TrendLine, StateHeat } from "./charts";
import SessionsTable from "./SessionsTable";
import CityBubbleMap from "./CityBubbleMap";

const RANGE_OPTIONS = [
  { value: 7, label: "7d" },
  { value: 30, label: "30d" },
  { value: 90, label: "90d" },
  { value: 0, label: "All" },
];

const TABS = ["Overview", "Sessions", "Geography", "Trends", "Pages", "Technology", "Acquisition"];

function PanelTitle({ children }) {
  return (
    <Typography
      variant="overline"
      sx={{ color: "primary.main", display: "block", mb: 1.5, letterSpacing: "0.12em" }}
    >
      {children}
    </Typography>
  );
}

const paperSx = { p: { xs: 2, md: 3 }, borderRadius: 4, height: "100%" };

function Panel({ title, children, ...grid }) {
  return (
    <Grid item xs={12} {...grid}>
      <Paper elevation={0} sx={paperSx}>
        {title && <PanelTitle>{title}</PanelTitle>}
        {children}
      </Paper>
    </Grid>
  );
}

export default function ActivityDashboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState(30);
  const [tab, setTab] = useState(0);

  const load = () => {
    setLoading(true);
    setError(null);
    api
      .get("/sk12_siteactivity")
      .then((r) =>
        setRows(
          (r.data || [])
            .slice()
            .sort((a, b) =>
              String(b.EventTimestampUtc || "").localeCompare(String(a.EventTimestampUtc || ""))
            )
        )
      )
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => withinDays(rows, range || null), [rows, range]);
  const summary = useMemo(() => summarize(filtered), [filtered]);
  const sstats = useMemo(() => sessionStats(filtered), [filtered]);

  const trend = useMemo(() => trendByDay(filtered), [filtered]);
  const trendSessions = useMemo(() => sessionsByDay(filtered), [filtered]);
  const deviceData = useMemo(() => countBy(filtered, "DeviceType"), [filtered]);
  const pageData = useMemo(() => countBy(filtered, "Path"), [filtered]);
  const stateData = useMemo(() => countBy(filtered, "State"), [filtered]);
  const cityData = useMemo(() => countBy(filtered, "City"), [filtered]);
  const countryData = useMemo(() => countBy(filtered, "Country"), [filtered]);
  const browserData = useMemo(() => countBy(filtered, "Browser"), [filtered]);
  const osData = useMemo(() => countBy(filtered, "OS"), [filtered]);
  const screenData = useMemo(() => countBy(filtered, "ScreenSize"), [filtered]);
  const referrerData = useMemo(() => countBy(filtered, "Referrer"), [filtered]);
  const langData = useMemo(() => countBy(filtered, "Language"), [filtered]);
  const entryData = useMemo(() => entryPages(filtered), [filtered]);
  const nvrData = useMemo(() => newVsReturning(filtered), [filtered]);

  const busiestDay = useMemo(
    () => trendSessions.reduce((best, d) => (d.count > (best?.count || 0) ? d : best), null),
    [trendSessions]
  );

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" sx={{ color: "primary.main", fontWeight: 700 }}>
          Site Activity
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={range}
            onChange={(_, v) => {
              if (v !== null) setRange(v);
            }}
          >
            {RANGE_OPTIONS.map((o) => (
              <ToggleButton key={o.value} value={o.value}>
                {o.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Button variant="outlined" onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
          No activity data yet. Tracking begins once visitors browse the site (and the analytics
          endpoint is live).
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {!error && rows.length === 0 && (
            <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
              No activity recorded yet. Once visitors start browsing the site, their page views will
              appear here.
            </Alert>
          )}

          {/* Always-visible KPI tiles */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <StatTile label="Total Views" value={summary.totalViews.toLocaleString()} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile label="Unique Visitors" value={summary.uniqueVisitors.toLocaleString()} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile label="Sessions" value={summary.uniqueSessions.toLocaleString()} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile
                label="Pages / Session"
                value={sstats.avgPagesPerSession.toFixed(1)}
                sublabel={`Avg. ${formatDuration(sstats.avgDurationMs)} on site`}
              />
            </Grid>
          </Grid>

          {/* Tab bar */}
          <Paper elevation={0} sx={{ borderRadius: 4, mb: 3 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ px: 1 }}
            >
              {TABS.map((t) => (
                <Tab key={t} label={t} sx={{ fontWeight: 600 }} />
              ))}
            </Tabs>
          </Paper>

          {/* OVERVIEW */}
          {tab === 0 && (
            <Grid container spacing={2.5}>
              <Panel title="Views over time">
                <TrendLine data={trend} showValues />
              </Panel>
              <Panel title="Device type" md={4}>
                <DonutChart data={deviceData} />
              </Panel>
              <Panel title="Top pages" md={8}>
                <BarList data={pageData} unit="views" />
              </Panel>
            </Grid>
          )}

          {/* SESSIONS */}
          {tab === 1 && (
            <Grid container spacing={2.5}>
              <Panel title="Sessions" md={4}>
                <Typography sx={{ color: "primary.main", fontWeight: 700, fontSize: { xs: "1.4rem", md: "1.7rem" } }}>
                  {sstats.sessions.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {sstats.avgPagesPerSession.toFixed(1)} pages · {formatDuration(sstats.avgDurationMs)} avg
                  · {(sstats.bounceRate * 100).toFixed(0)}% single-page
                </Typography>
              </Panel>
              <Panel title="New vs returning visitors" md={8}>
                <DonutChart data={nvrData} />
              </Panel>
              <Panel title="Sessions (click a row to expand its page views)">
                <SessionsTable events={filtered} />
              </Panel>
            </Grid>
          )}

          {/* GEOGRAPHY */}
          {tab === 2 && (
            <Grid container spacing={2.5}>
              <Panel title="Where visitors are — U.S. city heat map">
                <CityBubbleMap events={filtered} />
              </Panel>
              <Panel title="Visitors by state">
                <StateHeat data={stateData} />
              </Panel>
              <Panel title="Top states" md={4}>
                <BarList data={stateData} unit="views" />
              </Panel>
              <Panel title="Top cities" md={4}>
                <BarList data={cityData} unit="views" />
              </Panel>
              <Panel title="Countries" md={4}>
                <BarList data={countryData} unit="views" />
              </Panel>
            </Grid>
          )}

          {/* TRENDS */}
          {tab === 3 && (
            <Grid container spacing={2.5}>
              <Panel title="Sessions by date">
                <TrendLine data={trendSessions} height={300} showValues />
              </Panel>
              <Panel title="Busiest day" md={4}>
                <Typography sx={{ color: "primary.main", fontWeight: 700, fontSize: { xs: "1.4rem", md: "1.7rem" } }}>
                  {busiestDay ? `${busiestDay.count.toLocaleString()} sessions` : "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {busiestDay ? busiestDay.date : "No data yet"}
                </Typography>
              </Panel>
              <Panel title="Days tracked" md={4}>
                <Typography sx={{ color: "primary.main", fontWeight: 700, fontSize: { xs: "1.4rem", md: "1.7rem" } }}>
                  {trendSessions.length.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Distinct active days in range
                </Typography>
              </Panel>
              <Panel title="Avg sessions / active day" md={4}>
                <Typography sx={{ color: "primary.main", fontWeight: 700, fontSize: { xs: "1.4rem", md: "1.7rem" } }}>
                  {trendSessions.length ? (sstats.sessions / trendSessions.length).toFixed(1) : "0"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Across active days
                </Typography>
              </Panel>
            </Grid>
          )}

          {/* PAGES */}
          {tab === 4 && (
            <Grid container spacing={2.5}>
              <Panel title="Top pages" md={6}>
                <BarList data={pageData} unit="views" />
              </Panel>
              <Panel title="Entry pages (first page of a session)" md={6}>
                <BarList data={entryData} unit="sessions" />
              </Panel>
            </Grid>
          )}

          {/* TECHNOLOGY */}
          {tab === 5 && (
            <Grid container spacing={2.5}>
              <Panel title="Device type" md={4}>
                <DonutChart data={deviceData} />
              </Panel>
              <Panel title="Browsers" md={4}>
                <BarList data={browserData} unit="views" />
              </Panel>
              <Panel title="Operating systems" md={4}>
                <BarList data={osData} unit="views" />
              </Panel>
              <Panel title="Screen sizes" md={6}>
                <BarList data={screenData} unit="views" />
              </Panel>
              <Panel title="Languages" md={6}>
                <BarList data={langData} unit="views" />
              </Panel>
            </Grid>
          )}

          {/* ACQUISITION */}
          {tab === 6 && (
            <Grid container spacing={2.5}>
              <Panel title="Referrers" md={8}>
                <BarList data={referrerData} unit="views" />
              </Panel>
              <Panel title="New vs returning" md={4}>
                <DonutChart data={nvrData} />
              </Panel>
              <Panel title="Entry pages" md={12}>
                <BarList data={entryData} unit="sessions" />
              </Panel>
            </Grid>
          )}
        </>
      )}
    </Box>
  );
}
